import React, { useEffect } from 'react';
import ScreenWrapper from '../../components/ScreenWrapper/ScreenWrapper';
import withAuth from '../../auth/withAuth';
import { Color, Components, Font, LoadingIndicator, Theme } from '@oneamerica/dxp-ui-components';
import {
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { getLayoutType, convertBlobToBase64, openPDF } from '../../../utils';
import { faFilePdf } from '@fortawesome/free-solid-svg-icons/faFilePdf';
import {
  FontAwesomeIcon,
  FontAwesomeIconStyle,
} from '@fortawesome/react-native-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import responsiveStyles from './styles';
import { useSelector, useDispatch } from 'react-redux';
import { getTaxDocumentList, getTaxDocument } from '../../redux/taxDocument/action';
import ErrorModal from '../../components/ErrorModal';
import store from '../../redux/store/store';
import {taxDocuments} from "../../config/languages/en.json";
import { trackCustomEvent, trackPage } from '../../../utils/Analytics/AdobeAnalyticsUtils';
import { analyticsTags } from '../../../utils/Analytics/AnalyticsTags';

// Type definitions
interface FormDetails {
  taxYear: number | string;
  distributionCode: string;
  taxLocation: string;
  accountNumber: string;
  formType: string;
  formId: number | string;
  base64?: string;
}

interface AccountForm {
  formDetails: FormDetails;
}

type AccountForms = AccountForm[];

const TaxDocuments = (props) => {
  const dispatch = useDispatch();
  const {
    getTaxDocumentListData = {},
    getTaxDocumentData = null,
    isTaxDocumentListError = false, 
    isTaxDocumentError = false,
    loading = true,
    error = {},
    hasNYDocument = false,
  } = useSelector((state: any) => state.taxDocuments);
   const accessToken = props?.accessToken?.accessToken;

  useEffect(() => {
    trackPage(analyticsTags.TaxDocPageViewTags)
    const isDataEmpty = !getTaxDocumentListData || Object.keys(getTaxDocumentListData).length === 0;
    if (accessToken && isDataEmpty) {
      dispatch(getTaxDocumentList(accessToken));
    }
  }, [accessToken, getTaxDocumentListData]);

  useEffect(() => {
    if (getTaxDocumentData && getTaxDocumentData instanceof Blob) {
      convertBlobToBase64(getTaxDocumentData).then((base64String) => {
        openPDF(base64String, 'TaxDocument');
      });
    }
  }, [getTaxDocumentData]);

  const styles = responsiveStyles();
  const layoutType = getLayoutType();
  const isDesktopOrTablet = layoutType === 'desktop' || layoutType === 'tablet';

  // Organize the data from the API response into structured variables.
  const customerFullName = getTaxDocumentListData[0]?.customerDetails?.customerFullName || '';
  const accountNumbers = getTaxDocumentListData[1]?.accountNumbers ?? [];
  const taxDetails = getTaxDocumentListData[2]?.['1099R-Details'] ?? [];
  const combinedDisclosure = !!getTaxDocumentListData[0]?.customerDetails?.combinedDisclosure;
  const isTaxDetailsEmpty = !(
    Array.isArray(taxDetails) &&
    taxDetails.some((forms) => Array.isArray(forms) && forms.length > 0)
  );
  const handleDocumentPress = (formDetails: FormDetails) => {  
    const {
      base64,
      formId,
      taxYear,
      taxLocation,
      distributionCode,
    } = formDetails;

    // Use the base64 data from formDetails to open PDF and create a user-friendly name for the file.
    const fileName = `1099-R_${taxYear}_${taxLocation}_${distributionCode}.pdf`;
    trackCustomEvent(analyticsTags.TaxDocPDFClick(fileName));
    if (base64) {
      openPDF(base64, fileName);
      return;
    }
    if (!accessToken || !formId) return;

    dispatch(getTaxDocument(accessToken, formId));
    const unsubscribe = store.subscribe(() => {
      const state = store.getState();
      const docData = state.taxDocuments.getTaxDocumentData;

      if (docData && docData.blob instanceof Blob) {
        convertBlobToBase64(docData.blob).then((base64String) => {
          openPDF(base64String, fileName);
        });

        unsubscribe();
      }
    });
  };

  const prepareTableData = (accountForms: AccountForms, layout: string) => {
    const sortedForms = [...accountForms].sort((a, b) => {
      const yearA = Number.parseInt(a.formDetails.taxYear as string);
      const yearB = Number.parseInt(b.formDetails.taxYear as string);
      if (yearA !== yearB) {
        return yearB - yearA;
      }
      const stateA = a.formDetails.taxLocation || '';
      const stateB = b.formDetails.taxLocation || '';
      return stateA.localeCompare(stateB);
    });
    return sortedForms.map((form: AccountForm) => {
      const {formDetails} = form;
      return [
        <Text key={`taxYear-${formDetails.formId}`} style={{ textAlign: 'left' }}>{String(formDetails.taxYear)}</Text>,
        <Text key={`distributionCode-${formDetails.formId}`} style={{ textAlign: 'left' }} numberOfLines={2} allowFontScaling={false}>{formDetails.distributionCode}</Text>,
        <Text key={`taxLocation-${formDetails.formId}`} style={{ textAlign: 'left' }}>{formDetails.taxLocation}</Text>,
        <TouchableOpacity
          key={`pdfIcon-${formDetails.formId}`}
          onPress={() => handleDocumentPress(formDetails)}
          style={{
            justifyContent: 'center',
            padding: 2,
            borderWidth: 0, // Remove any border
          }}
          activeOpacity={0.7} // Smooth opacity change on press
          accessibilityRole="button"
          accessibilityLabel="Download PDF">
          <FontAwesomeIcon
            testID="pdfIcon"
            style={styles.faFilePdfIcon as FontAwesomeIconStyle}
            icon={faFilePdf as IconProp}
            color={Color.oaBlueLight}
          />
        </TouchableOpacity>,
      ];
    });
  };
  const centeredHeaderText = (label: string) => (
    <Text numberOfLines={1} ellipsizeMode="tail" style={{ fontWeight: 'bold', textAlign: 'left' }}>
      {label}
    </Text>
  );
  const renderAccountTable = (
    accountForms: AccountForms,
    accountIndex: number,
    viewType: 'desktop' | 'tablet' | 'mobile' = 'desktop',
  ) => {
    const accountNumber = accountNumbers[accountIndex];
    const tableData = prepareTableData(accountForms, viewType);

    let wrapperStyle = styles.tableWrapper;
    let tableStyle = styles.tableStyle;
    let titleStyle = styles.accountTitle;
    let flexArr: number[] = [0.15, 0.4, 0.25, 0.2];
    let headerColumns: (string | JSX.Element)[] = [];
    const isTablet = layoutType === 'tablet';
    if (viewType === 'tablet') {
      wrapperStyle = styles.tabletTableWrapper;
      flexArr = [0.22, 0.35, 0.23, 0.2];
      headerColumns = [
        <View key="year-column" style={{ flex: 1 }}>
          <Text style={styles.headerTextCell}>{taxDocuments.yearHeader}</Text>
        </View>,
        <View key="distribution-column" style={{ flex: 2 }}>
          <Text style={[styles.headerTextCell, isTablet && { fontSize: 14, lineHeight: 18, paddingVertical: 6 }]} numberOfLines={2}>{taxDocuments.distributionCodeHeader}</Text>
        </View>,
        <View key="state-column" style={{ flex: 1 }}>
          <Text style={styles.headerTextCell}>{taxDocuments.stateHeader}</Text>
        </View>,
        <View key="file-column" style={{ flex: 1 }}>
          <Text style={styles.headerTextCell}>{taxDocuments.fileHeader}</Text>
        </View>,
      ];
    } else if (viewType === 'mobile') {
      wrapperStyle = styles.mobileTableWrapper;
      tableStyle = styles.mobileTableStyle;
      titleStyle = styles.mobileAccountTitle;
      flexArr = [1.1, 2, 1.2, 1];
      headerColumns = [
        <View key="year-column" style={{ flex: 1 }}>
          <Text style={styles.headerTextCell} numberOfLines={2}>{taxDocuments.yearHeader}</Text>
        </View>,
        <View key="distribution-column" style={{ flex: 2 }}>
          <Text style={styles.headerTextCell} numberOfLines={2}>{taxDocuments.distributionCodeHeader}</Text>
        </View>,
        <View key="state-column" style={{ flex: 1 }}>
          <Text style={styles.headerTextCell} numberOfLines={1}>{taxDocuments.stateHeader}</Text>
        </View>,
        <View key="file-column" style={{ flex: 1 }}>
          <Text style={styles.headerTextCell} numberOfLines={1}>{taxDocuments.fileHeader}</Text>
        </View>,
      ];
    } else {
        // Desktop
      headerColumns = [
        centeredHeaderText('Year'),
        centeredHeaderText('Distribution Code'),
        centeredHeaderText('State'),
        centeredHeaderText('File'),
      ];
    }

    return (
      <View key={`account-${accountIndex}`} style={wrapperStyle}>
        <Text style={titleStyle}>{accountNumber}</Text>

        <Components.OATable
          tableHeight={tableData.length > 6 ? 200 : undefined}
          flexArr={flexArr}
          style={tableStyle}
          headerColumns={headerColumns}
          blueRows={true}
          tableData={tableData}
          hasNoData={tableData.length > 0}
          altRows={true}
        />
      </View>
    );
  };
  const getTaxDocumentsDescription = () => {
    if (error?.status === 404 || isTaxDetailsEmpty) {
    return (
      <View>
         <Text style={styles.descriptionText}>{taxDocuments.noTaxDocumentsAvailableDesc}</Text>
          <Text style={[styles.descriptionText, { marginTop: 8 }]}>
            {taxDocuments.noTaxDocumentsAvailableDescLine2}
          </Text>
        </View>
      );
    }

    return (
      <View>
        <Text style={styles.descriptionText}>
          {taxDocuments.taxDocumentsDesc}
        </Text>
        <Text style={[styles.descriptionText, { marginTop: 8 }]}>
          {taxDocuments.taxDocumentsDescLine2}
        </Text>
      </View>
    );
  };
  const renderHeader = () => (
      <View style={styles.headerSection}>
        <Text style={styles.taxDocumentsTitle}>{taxDocuments.taxDocumentsHeader}</Text>
        <Components.Divider />
        {getTaxDocumentsDescription()}
      </View>
    );

const renderTablesLayout = () => (
  <View style={styles.tablesContainer}>
    {/* Desktop and Tablet Layout - Side by side tables */}
    {isDesktopOrTablet ? (
      <View style={layoutType === 'desktop' ? styles.desktopTablesRow : styles.tabletTablesRow}>
        {accountNumbers.map((_, index) => {
          const accountForms = taxDetails[index];
          if (!accountForms || accountForms.length === 0) return null;
          return renderAccountTable(accountForms, index, layoutType);
        })}
      </View>
    ) : (
      <View style={styles.mobileTablesColumn}>
        {accountNumbers.map((_, index) => {
          const accountForms = taxDetails[index];
          if (!accountForms || accountForms.length === 0) return null;
          return renderAccountTable(accountForms, index, 'mobile');
        })}
      </View>
    )}
  </View>
);

  const showError = (isTaxDocumentListError || isTaxDocumentError) && error?.status !== 404 || hasNYDocument;

  let content: JSX.Element;

  if (loading) {
        // Loading State
    content = <LoadingIndicator loading={true} testID="loading-indicator" />;
  } else if (showError) {
       // Error State
    content = <ErrorModal />;
  } else {
    content = (
      <View style={{ paddingHorizontal: 8 }}>
        {customerFullName && (
          <View
            className="hidden lg:flex flex-col md:flex-row justify-between items-start md:items-center mt-5 mb-5 pr-4 md:pr-6">
            <Components.Text.H1 style={{color: Theme.Color.oaBlue, marginBottom:8, fontFamily: Font.bold.fontFamily}}>
              {customerFullName || ' '}
            </Components.Text.H1>
          </View>
        )}
          <View style={{ width: '100%', maxWidth: 1024, }}>
            <Components.Card testID="taxDocumentsCard" type="default" orientation="vertical">
              {renderHeader()}
              {!isTaxDetailsEmpty && renderTablesLayout()}
            </Components.Card>
            {combinedDisclosure &&
              accountNumbers.some((acct, index) => acct.includes('*') && taxDetails[index]?.length > 0) && (
                <Text
                  style={[styles.descriptionText, { marginTop: 16 }]}>
                  {taxDocuments.combinedDisclosureMsg}
                </Text>
              )}
            <Text
              style={[styles.descriptionText, {
                marginTop: combinedDisclosure ? 16 : 32,
                marginBottom: 32,
              }]}>
              {taxDocuments.legalSubDesc}
            </Text>
          </View>
        </View>
    );
  }
  return <ScreenWrapper scrollFooter>{content}</ScreenWrapper>;
};

export default withAuth(TaxDocuments);
